import React, { useMemo } from 'react';

import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import { AssetsIcon } from '@atlaskit/logo';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import {
	getProductAndIDFromARI,
	isSearchResultAssetsObject,
	isSearchResultAssetsObjectSchema,
	isSearchResultAssetsObjectType,
	type SearchAssetsPartial,
} from '@atlassian/search-client';
import { highlightMatchedText } from '@atlassian/search-common';

import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { MentionsLinkedResults } from '../../../../../../common/ui/linked-results';
import { getAssetsEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { truncateTextWithQuery } from '../../../../../../common/utils/truncate-text-with-query';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../controllers/store/bootstrap/accessors';
import { useSearchQuery, useSelectedProduct } from '../../../../../../controllers/store/search';

interface AssetsSearchResultProps {
	partial: SearchAssetsPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}
const SchemaMetadata = ({ schemaName }: { schemaName: string }) => {
	return (
		<Inline space="space.050" alignBlock="center">
			<AssetsSchemaIcon size="small" label="" />
			<Text color="inherit">{schemaName}</Text>
		</Inline>
	);
};
export const AssetsSearchResult = ({
	partial,
	position,
}: AssetsSearchResultProps): React.JSX.Element => {
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const [searchQuery] = useSearchQuery();
	const appMode = useApplicationMode();

	const { id, title, type, description, lastModifiedDate } = partial;
	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(id);
		return {
			id,
			containerId: null,
			spaceId: null,
			contentType: type,
			entityType: type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [id, type]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const getLastModified = useMemo(() => {
		if (!lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={lastModifiedDate} />;
	}, [lastModifiedDate]);

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return title;
		}
		return enrichExcerpt(title);
	}, [title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = truncateTextWithQuery(description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	const headerMetadataParts = useMemo(() => {
		if (!isSearchResultAssetsObjectSchema(partial)) {
			return [
				['type', <SearchResultType type={metadata.entityType} />],
				['schema', <SchemaMetadata schemaName={partial.objectSchemaEntity.name} />],
				['lastModified', getLastModified],
			];
		}
		return [
			['type', <SearchResultType type={metadata.entityType} />],
			['lastModified', getLastModified],
		];
	}, [partial, metadata.entityType, getLastModified]);

	const getUrl = (partial: SearchAssetsPartial) => {
		if (isSearchResultAssetsObjectSchema(partial)) {
			return partial.objectSchemaEntity.links.web;
		} else if (isSearchResultAssetsObjectType(partial)) {
			return partial.objectSchemaEntity.links.web;
		} else {
			return partial.objectEntity.links.web;
		}
	};

	const getIcon = () => {
		if (isSearchResultAssetsObjectSchema(partial)) {
			return getAssetsEntityIcon({ iconUrl: null, type: partial.type, size: 'medium' });
		} else if (isSearchResultAssetsObjectType(partial)) {
			return getAssetsEntityIcon({
				iconUrl: partial.objectTypeEntity.icon.url48,
				type: partial.type,
				size: 'medium',
			});
		} else if (isSearchResultAssetsObject(partial)) {
			return getAssetsEntityIcon({
				iconUrl: partial.objectEntity.avatar.url48,
				type: partial.type,
				size: 'medium',
			});
		}
		return <AssetsIcon size="medium" />;
	};
	return (
		<SearchResult
			{...partial}
			product={metadata.product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={getUrl(partial)}
			icon={getIcon()}
			title={enrichedTitle}
			headerMetadataParts={headerMetadataParts}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			footer={
				<MentionsLinkedResults
					linkedResults={partial.linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={partial}
					url={getUrl(partial)}
					position={position}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
				/>
			}
		/>
	);
};
